import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

import { api } from "@/lib/api";
import { queryKeys } from "@/lib/queryKeys";
import { compressImage } from "@/lib/imageUtils";
import { OrgRolesTab } from "@/components/shared/tab/OrgRolesTab";
import type { Organization } from "@/types";

export function OrganizationsPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [showAdd, setShowAdd] = useState(false);
  const [search, setSearch] = useState("");

  const { data, isLoading } = useQuery({
    queryKey: queryKeys.organizations,
    queryFn: () => api.listOrganizations(),
  });
  const organizations = data?.organizations ?? [];

  const filtered = organizations.filter((o) => {
    const q = search.toLowerCase().trim();
    return !q || o.name.toLowerCase().includes(q) || o.slug.toLowerCase().includes(q);
  });

  const refresh = () => queryClient.invalidateQueries({ queryKey: queryKeys.organizations });

  return (
    <div className="flex h-full flex-col overflow-hidden">
      {/* Sticky header */}
      <div className="shrink-0 shadow-sm bg-white px-3 py-4 sm:px-6">
        <div className="mx-auto max-w-6xl">
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-2">
          <h2 className="text-[28px] font-bold leading-[1.43] text-near-black">Organizations</h2>
          <span className="rounded-badge bg-near-black px-2.5 py-0.5 text-xs font-semibold tabular-nums text-white">
            {organizations.length}
          </span>
        </div>
        <div className="flex flex-1 items-center gap-2 sm:max-w-md">
          <div className="relative flex-1">
            <svg className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-text-secondary" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z" />
            </svg>
            <input
              className="w-full rounded-btn border border-border-card bg-white py-2 pl-10 pr-3 text-sm text-near-black placeholder:text-text-secondary focus:outline-none focus:shadow-focus"
              placeholder="Search organizations..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <button
            onClick={() => setShowAdd(true)}
            className="shrink-0 rounded-btn bg-near-black px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-rausch"
          >
            + Add organization
          </button>
        </div>
      </div>
        </div>
      </div>

      {/* Scrollable body */}
      <div className="flex-1 overflow-y-auto">
        <div className="mx-auto max-w-6xl px-3 py-4 sm:px-6 sm:py-6">
      {isLoading ? (
        <p className="text-text-secondary">Loading...</p>
      ) : filtered.length === 0 ? (
        <div className="rounded-card border-2 border-dashed border-border-card bg-white p-10 text-center text-text-secondary">
          {organizations.length === 0
            ? "No organizations yet. Create one to get started."
            : "No organizations match your search."}
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {filtered.map((org) => (
            <OrgCard key={org.id} org={org} onRefresh={refresh} onViewAnalytics={(id) => navigate(`/organizations/${id}/analytics`)} />
          ))}
        </div>
      )}
        </div>
      </div>

      {showAdd && <AddOrgDialog onClose={() => setShowAdd(false)} onCreated={refresh} />}
    </div>
  );
}

function OrgCard({ org, onRefresh, onViewAnalytics }: { org: Organization; onRefresh: () => void; onViewAnalytics: (id: number) => void }) {
  const [showSettings, setShowSettings] = useState(false);
  const initial = org.name.charAt(0).toUpperCase();

  return (
    <>
      <div className="overflow-hidden rounded-card bg-white shadow-card cursor-pointer transition-shadow hover:shadow-md" onClick={() => onViewAnalytics(org.id)}>
        {/* Banner area */}
        <div className="relative h-24 bg-gradient-to-br from-gray-100 to-gray-200">
          {org.banner ? (
            <img src={org.banner} alt="" className="h-full w-full bg-white object-cover" />
          ) : (
            <div className="h-full w-full bg-gradient-to-br from-gray-100 to-gray-200" />
          )}
          {/* Gear button */}
          <button
            onClick={(e) => { e.stopPropagation(); setShowSettings(true); }}
            className="absolute right-2 top-2 rounded-btn bg-white/80 p-1.5 text-text-secondary backdrop-blur-sm transition-colors hover:bg-white hover:text-near-black"
            title="Organization settings"
          >
            <svg className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.325.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 0 1 1.37.49l1.296 2.247a1.125 1.125 0 0 1-.26 1.431l-1.003.827c-.293.241-.438.613-.43.992a7.723 7.723 0 0 1 0 .255c-.008.378.137.75.43.991l1.004.827c.424.35.534.955.26 1.43l-1.298 2.247a1.125 1.125 0 0 1-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.47 6.47 0 0 1-.22.128c-.331.183-.581.495-.644.869l-.213 1.281c-.09.543-.56.94-1.11.94h-2.594c-.55 0-1.019-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 0 1-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 0 1-1.369-.49l-1.297-2.247a1.125 1.125 0 0 1 .26-1.431l1.004-.827c.292-.24.437-.613.43-.991a6.932 6.932 0 0 1 0-.255c.007-.38-.138-.751-.43-.992l-1.004-.827a1.125 1.125 0 0 1-.26-1.43l1.297-2.247a1.125 1.125 0 0 1 1.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.086.22-.128.332-.183.582-.495.644-.869l.214-1.28Z" />
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
            </svg>
          </button>
          {/* Logo overlapping banner bottom */}
          <div className="absolute -bottom-5 left-4">
            {org.logo ? (
              <img src={org.logo} alt={org.name} className="h-12 w-12 rounded-full border-2 border-white bg-white object-cover shadow-md" />
            ) : (
              <div className="flex h-12 w-12 items-center justify-center rounded-full border-2 border-white bg-rausch text-base font-bold text-white shadow-sm">
                {initial}
              </div>
            )}
          </div>
        </div>

        {/* Content */}
        <div className="px-4 pb-4 pt-8">
          <h3 className="text-sm font-semibold text-near-black">{org.name}</h3>
          <p className="text-[11px] text-text-secondary">{org.slug}</p>

          <div className="mt-3 flex justify-between border-t border-surface-secondary pt-3">
            <div className="flex-1 text-center">
              <p className="text-lg font-bold tabular-nums text-near-black">{org.user_count ?? 0}</p>
              <p className="text-[10px] font-medium text-text-secondary">Users</p>
            </div>
            <div className="flex-1 text-center">
              <p className="text-lg font-bold tabular-nums text-near-black">{org.candidate_count ?? 0}</p>
              <p className="text-[10px] font-medium text-text-secondary">Candidates</p>
            </div>
            <div className="flex-1 text-center">
              <p className="text-lg font-bold tabular-nums text-near-black">{org.archived_count ?? 0}</p>
              <p className="text-[10px] font-medium text-text-secondary">Archived</p>
            </div>
            <div className="flex-1 text-center">
              <p className="text-lg font-bold tabular-nums text-near-black">{org.position_count ?? 0}</p>
              <p className="text-[10px] font-medium text-text-secondary">Positions</p>
            </div>
            <div className="flex-1 text-center">
              <p className="text-lg font-bold tabular-nums text-near-black">{org.matrix_count ?? 0}</p>
              <p className="text-[10px] font-medium text-text-secondary">Matrices</p>
            </div>
          </div>
        </div>
      </div>

      {showSettings && (
        <OrgSettingsDialog
          org={org}
          onClose={() => setShowSettings(false)}
          onRefresh={() => { setShowSettings(false); onRefresh(); }}
          onDeleted={() => { setShowSettings(false); onRefresh(); }}
        />
      )}
    </>
  );
}

function OrgSettingsDialog({
  org,
  onClose,
  onRefresh,
  onDeleted,
}: {
  org: Organization;
  onClose: () => void;
  onRefresh: () => void;
  onDeleted: () => void;
}) {
  const [activeTab, setActiveTab] = useState<"general" | "roles">("general");
  const [name, setName] = useState(org.name);
  const [slug, setSlug] = useState(org.slug);

  const [previewLogo, setPreviewLogo] = useState<string | null>(org.logo);
  const [pendingLogo, setPendingLogo] = useState<string | null | undefined>(undefined);
  const [logoUploading, setLogoUploading] = useState(false);

  const [previewBanner, setPreviewBanner] = useState<string | null>(org.banner);
  const [pendingBanner, setPendingBanner] = useState<string | null | undefined>(undefined);
  const [bannerUploading, setBannerUploading] = useState(false);

  const [confirmDelete, setConfirmDelete] = useState(false);
  const [error, setError] = useState("");
  const logoInputRef = useRef<HTMLInputElement>(null);
  const bannerInputRef = useRef<HTMLInputElement>(null);

  const saveMutation = useMutation({
    mutationFn: () => {
      const body: Parameters<typeof api.updateOrganization>[1] = {};
      if (name !== org.name) body.name = name;
      if (slug !== org.slug) body.slug = slug;
      if (pendingLogo !== undefined) body.logo = pendingLogo;
      if (pendingBanner !== undefined) body.banner = pendingBanner;
      return api.updateOrganization(org.id, body);
    },
    onSuccess: onRefresh,
    onError: (e: Error) => setError(e.message || "Failed to save"),
  });

  const deleteMutation = useMutation({
    mutationFn: () => api.deleteOrganization(org.id),
    onSuccess: onDeleted,
    onError: (e: Error) => setError(e.message || "Failed to delete"),
  });

  const handleLogoFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = "";
    setLogoUploading(true);
    try {
      const { dataUrl, mime } = await compressImage(file, 256, 256, 0.85);
      setPreviewLogo(dataUrl);
      const { url } = await api.uploadImage({ imageData: dataUrl, mime, slot: `org-logo-${org.id}` });
      setPendingLogo(url);
    } finally {
      setLogoUploading(false);
    }
  };

  const handleBannerFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = "";
    setBannerUploading(true);
    try {
      const { dataUrl, mime } = await compressImage(file, 1200, 400, 0.82);
      setPreviewBanner(dataUrl);
      const { url } = await api.uploadImage({ imageData: dataUrl, mime, slot: `org-banner-${org.id}` });
      setPendingBanner(url);
    } finally {
      setBannerUploading(false);
    }
  };

  const autoSlug = (val: string) => {
    setName(val);
    setSlug(val.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, ""));
  };

  const initial = name.charAt(0).toUpperCase() || org.name.charAt(0).toUpperCase();
  const isDirty =
    name !== org.name ||
    slug !== org.slug ||
    pendingLogo !== undefined ||
    pendingBanner !== undefined;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 px-4" onClick={onClose}>
      <div
        className="w-full max-w-2xl rounded-card bg-white shadow-card"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-surface-secondary px-5 py-4">
          <div>
            <h3 className="text-base font-semibold text-near-black">{org.name}</h3>
            <p className="text-[11px] text-text-secondary">{org.slug}</p>
          </div>
          <button onClick={onClose} className="rounded-btn p-1 text-text-secondary hover:text-near-black">
            <svg className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Tab bar */}
        <div className="flex border-b border-surface-secondary px-5">
          {(["general", "roles"] as const).map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={[
                "mr-4 border-b-2 py-2.5 text-sm font-medium capitalize transition-colors",
                activeTab === tab
                  ? "border-near-black text-near-black"
                  : "border-transparent text-text-secondary hover:text-near-black",
              ].join(" ")}
            >
              {tab}
            </button>
          ))}
        </div>

        {activeTab === "roles" ? (
          <div className="max-h-[calc(100vh-14rem)] overflow-y-auto">
            <OrgRolesTab orgId={org.id} />
          </div>
        ) : (
        <div className="max-h-[calc(100vh-14rem)] overflow-y-auto px-5 py-5 space-y-5">
          {/* Banner section */}
          <div>
            <p className="mb-2 text-xs font-semibold uppercase tracking-[0.32px] text-text-secondary">Banner</p>
            <div className="overflow-hidden rounded-btn border border-border-card">
              <div className="relative h-20 bg-gradient-to-br from-gray-100 to-gray-200">
                {previewBanner ? (
                  <img src={previewBanner} alt="" className="h-full w-full bg-white object-cover" />
                ) : null}
                {bannerUploading && (
                  <div className="absolute inset-0 flex items-center justify-center bg-black/40">
                    <svg className="h-5 w-5 animate-spin text-white" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                  </div>
                )}
              </div>
            </div>
            <div className="mt-2 flex items-center gap-3">
              <button
                onClick={() => bannerInputRef.current?.click()}
                disabled={bannerUploading}
                className="rounded-btn border border-border-card px-3 py-1.5 text-xs font-medium text-near-black transition-colors hover:bg-surface-secondary disabled:opacity-50"
              >
                {previewBanner ? "Change banner" : "Upload banner"}
              </button>
              {previewBanner && (
                <button
                  onClick={() => { setPreviewBanner(null); setPendingBanner(null); }}
                  className="text-xs text-text-secondary transition-colors hover:text-error-text"
                >
                  Remove
                </button>
              )}
              <p className="text-[10px] text-text-secondary">Recommended: 1200×400px</p>
            </div>
            <input ref={bannerInputRef} type="file" accept="image/*" className="hidden" onChange={handleBannerFile} />
          </div>

          {/* Logo section */}
          <div>
            <p className="mb-2 text-xs font-semibold uppercase tracking-[0.32px] text-text-secondary">Logo</p>
            <div className="flex items-center gap-4">
              <div className="relative shrink-0">
                {previewLogo ? (
                  <img src={previewLogo} alt={name} className="h-16 w-16 rounded-full bg-white object-cover" />
                ) : (
                  <div className="flex h-16 w-16 items-center justify-center rounded-full bg-rausch text-xl font-bold text-white">
                    {initial}
                  </div>
                )}
                {logoUploading && (
                  <div className="absolute inset-0 flex items-center justify-center rounded-full bg-black/50">
                    <svg className="h-5 w-5 animate-spin text-white" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                  </div>
                )}
              </div>
              <div className="flex flex-col gap-2">
                <button
                  onClick={() => logoInputRef.current?.click()}
                  disabled={logoUploading}
                  className="rounded-btn border border-border-card px-3 py-1.5 text-xs font-medium text-near-black transition-colors hover:bg-surface-secondary disabled:opacity-50"
                >
                  {previewLogo ? "Change logo" : "Upload logo"}
                </button>
                {previewLogo && (
                  <button
                    onClick={() => { setPreviewLogo(null); setPendingLogo(null); }}
                    className="text-left text-xs text-text-secondary transition-colors hover:text-error-text"
                  >
                    Remove logo
                  </button>
                )}
                <p className="text-[10px] text-text-secondary">PNG, JPG, WebP · max 5 MB</p>
              </div>
              <input ref={logoInputRef} type="file" accept="image/*" className="hidden" onChange={handleLogoFile} />
            </div>
          </div>

          {/* Name & Slug */}
          <div className="space-y-3">
            <div>
              <label className="text-xs font-semibold uppercase tracking-[0.32px] text-text-secondary">Name</label>
              <input
                type="text"
                value={name}
                onChange={(e) => autoSlug(e.target.value)}
                className="mt-1 w-full rounded-btn border border-border-card bg-white px-3 py-2 text-sm text-near-black focus:outline-none focus:shadow-focus"
                placeholder="Acme Corp"
              />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-[0.32px] text-text-secondary">Slug</label>
              <input
                type="text"
                value={slug}
                onChange={(e) => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ""))}
                className="mt-1 w-full rounded-btn border border-border-card bg-white px-3 py-2 text-sm text-near-black focus:outline-none focus:shadow-focus"
                placeholder="acme-corp"
              />
            </div>
          </div>

          {error && (
            <p className="rounded-badge bg-rose-100 px-3 py-2 text-xs font-medium text-error-text">{error}</p>
          )}

          {/* Save */}
          <div className="flex justify-end gap-2">
            <button
              onClick={onClose}
              className="rounded-btn px-4 py-2 text-sm font-medium text-text-secondary transition-colors hover:text-near-black"
            >
              Cancel
            </button>
            <button
              onClick={() => { setError(""); saveMutation.mutate(); }}
              disabled={!isDirty || saveMutation.isPending || logoUploading || bannerUploading}
              className="rounded-btn bg-near-black px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-rausch disabled:opacity-40"
            >
              {saveMutation.isPending ? "Saving…" : "Save changes"}
            </button>
          </div>

          {/* Danger zone */}
          <div className="rounded-btn border border-rose-200 bg-rose-50 p-3">
            <p className="mb-2 text-xs font-semibold text-error-text">Danger zone</p>
            {!confirmDelete ? (
              <button
                onClick={() => setConfirmDelete(true)}
                className="rounded-btn border border-rose-300 px-3 py-1.5 text-xs font-medium text-error-text transition-colors hover:bg-rose-100"
              >
                Delete organization
              </button>
            ) : (
              <div className="space-y-2">
                <p className="text-xs text-error-text">
                  This will permanently delete <strong>{org.name}</strong> and all its data. Are you sure?
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={() => deleteMutation.mutate()}
                    disabled={deleteMutation.isPending}
                    className="rounded-btn bg-rose-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-rose-700 disabled:opacity-50"
                  >
                    {deleteMutation.isPending ? "Deleting…" : "Yes, delete"}
                  </button>
                  <button
                    onClick={() => setConfirmDelete(false)}
                    className="text-xs text-text-secondary hover:text-near-black"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
        )}
      </div>
    </div>
  );
}

function AddOrgDialog({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const autoSlug = (val: string) => {
    setName(val);
    setSlug(val.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, ""));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setSaving(true);
    try {
      await api.createOrganization({ name, slug });
      onCreated();
      onClose();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Failed to create organization");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30" onClick={onClose}>
      <div className="w-full max-w-sm rounded-card bg-white p-6 shadow-card" onClick={(e) => e.stopPropagation()}>
        <h3 className="mb-4 text-lg font-semibold text-near-black">New organization</h3>
        <form onSubmit={handleSubmit} className="flex flex-col gap-3">
          <div>
            <label className="text-xs font-semibold uppercase tracking-[0.32px] text-text-secondary">Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => autoSlug(e.target.value)}
              className="mt-1 w-full rounded-btn border border-border-card bg-white px-3 py-2 text-sm text-near-black focus:outline-none focus:shadow-focus"
              placeholder="Acme Corp"
              autoFocus
              required
            />
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-[0.32px] text-text-secondary">Slug</label>
            <input
              type="text"
              value={slug}
              onChange={(e) => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ""))}
              className="mt-1 w-full rounded-btn border border-border-card bg-white px-3 py-2 text-sm text-near-black focus:outline-none focus:shadow-focus"
              placeholder="acme-corp"
              required
            />
            <p className="mt-1 text-[10px] text-text-secondary">Lowercase letters, numbers, and hyphens only</p>
          </div>

          {error && (
            <p className="rounded-badge bg-rose-100 px-3 py-2 text-xs font-medium text-error-text">{error}</p>
          )}

          <div className="mt-2 flex justify-end gap-2">
            <button type="button" onClick={onClose} className="rounded-btn px-4 py-2 text-sm font-medium text-text-secondary transition-colors hover:text-near-black">
              Cancel
            </button>
            <button type="submit" disabled={saving} className="rounded-btn bg-near-black px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-rausch disabled:opacity-50">
              {saving ? "Creating..." : "Create"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
